import { LocalVideoTrack, Room } from 'twilio-video';
import { useCallback, useEffect, useState } from 'react';
import {
  BACKGROUND_FILTER_VIDEO_CONSTRAINTS,
  DEFAULT_VIDEO_CONSTRAINTS,
  SELECTED_BACKGROUND_SETTINGS_KEY,
} from '../../../constants';
import {
  GaussianBlurBackgroundProcessor,
  ImageFit,
  isSupported,
  VirtualBackgroundProcessor,
} from '@twilio/video-processors';

import Circet from '../../../images/Circet.png';
import EarthRodSample from '../../../images/Earth-rod-sample.jpg';
import EarthInspectionBoxes from '../../../images/Earth-Inspection-Boxes.png';
import Screenshot1 from '../../../images/Screenshot1.png';
import Screenshot2 from '../../../images/Screenshot2.png';
import Screenshot3 from '../../../images/Screenshot3.png';
import Screenshot4 from '../../../images/Screenshot4.png';
import Screenshot5 from '../../../images/Screenshot5.png';
import Screenshot6 from '../../../images/Screenshot6.png';

import { Thumbnail } from '../../BackgroundSelectionDialog/BackgroundThumbnail/BackgroundThumbnail';
import { useLocalStorageState } from '../../../hooks/useLocalStorageState/useLocalStorageState';

export interface BackgroundSettings {
  type: Thumbnail;
  index?: number;
  isPresentationMode?: boolean;
}

const imageNames: string[] = [
  'Circet',
  'Earth Rod Sample',
  'Earth Inspection Boxes',
  'Screenshot 1',
  'Screenshot 2',
  'Screenshot 3',
  'Screenshot 4',
  'Screenshot 5',
  'Screenshot 6',
];

const images = [
  Circet,
  EarthRodSample,
  EarthInspectionBoxes,
  Screenshot1,
  Screenshot2,
  Screenshot3,
  Screenshot4,
  Screenshot5,
  Screenshot6,
];

const rawImagePaths = [
  Circet,
  EarthRodSample,
  EarthInspectionBoxes,
  Screenshot1,
  Screenshot2,
  Screenshot3,
  Screenshot4,
  Screenshot5,
  Screenshot6,
];

let imageElements = new Map();

const getImage = (index: number, scale: number = 1): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const cacheKey = `${index}_${scale}`;
    if (imageElements.has(cacheKey)) {
      return resolve(imageElements.get(cacheKey));
    }
    const img = new Image();
    img.onload = () => {
      // Create a canvas to scale the image
      const canvas = document.createElement('canvas');
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const scaledImg = new Image();
        scaledImg.src = canvas.toDataURL();
        scaledImg.onload = () => {
          imageElements.set(cacheKey, scaledImg);
          resolve(scaledImg);
        };
        scaledImg.onerror = reject;
      } else {
        reject(new Error('Failed to get canvas context'));
      }
    };
    img.onerror = reject;
    img.src = rawImagePaths[index];
  });
};

export const backgroundConfig = {
  imageNames,
  images,
};

const virtualBackgroundAssets = '/virtualbackground';
const DEFAULT_BACKGROUND_IMAGE_INDEX = 0;
let blurProcessor: GaussianBlurBackgroundProcessor;
let virtualBackgroundProcessor: VirtualBackgroundProcessor;

export default function useBackgroundSettings(videoTrack: LocalVideoTrack | undefined, room?: Room | null) {
  const [backgroundSettings, setBackgroundSettings] = useLocalStorageState<BackgroundSettings>(
    SELECTED_BACKGROUND_SETTINGS_KEY,
    { type: 'none' } // Default to no background; Circet is shown as a static placeholder when video is off.
  );

  const [isPresentationMode, setIsPresentationMode] = useState(false);

  const setCaptureConstraints = useCallback(async () => {
    const { mediaStreamTrack, processor } = videoTrack ?? {};
    const { type } = backgroundSettings;
    if (type === 'none' && processor) {
      return mediaStreamTrack?.applyConstraints(DEFAULT_VIDEO_CONSTRAINTS as MediaTrackConstraints);
    } else if (type !== 'none' && !processor) {
      return mediaStreamTrack?.applyConstraints(BACKGROUND_FILTER_VIDEO_CONSTRAINTS as MediaTrackConstraints);
    }
  }, [backgroundSettings, videoTrack]);

  const removeProcessor = useCallback(() => {
    if (videoTrack && videoTrack.processor) {
      videoTrack.removeProcessor(videoTrack.processor);
    }
  }, [videoTrack]);

  const addProcessor = useCallback(
    (processor: GaussianBlurBackgroundProcessor | VirtualBackgroundProcessor) => {
      if (!videoTrack || videoTrack.processor === processor) {
        return;
      }
      removeProcessor();
      videoTrack.addProcessor(processor, {
        inputFrameBufferType: 'videoframe',
        outputFrameBufferContextType: 'bitmaprenderer',
      });
    },
    [videoTrack, removeProcessor]
  );

  const togglePresentationMode = useCallback(
    (enabled: boolean) => {
      setIsPresentationMode(enabled);

      // When presentation mode is on, disable the video track
      // When off, re-enable it
      if (videoTrack && videoTrack.mediaStreamTrack) {
        videoTrack.mediaStreamTrack.enabled = !enabled;
      }
    },
    [videoTrack]
  );

  useEffect(() => {
    if (!isSupported) {
      return;
    }
    // Apply video processors even before joining the room to show backgrounds in preview
    const handleProcessorChange = async () => {
      if (!blurProcessor) {
        blurProcessor = new GaussianBlurBackgroundProcessor({
          assetsPath: virtualBackgroundAssets,
        });
        await blurProcessor.loadModel();
      }
      if (!virtualBackgroundProcessor) {
        virtualBackgroundProcessor = new VirtualBackgroundProcessor({
          assetsPath: virtualBackgroundAssets,
          backgroundImage: await getImage(DEFAULT_BACKGROUND_IMAGE_INDEX, 0.6),
          fitType: ImageFit.Contain,
        });
        await virtualBackgroundProcessor.loadModel();
      }

      // Don't require localParticipant - allow backgrounds in preview mode
      if (!videoTrack) {
        return;
      }

      // Switch to 640x480 dimensions on browsers that do not support
      // WebAssembly SIMD to achieve optimum performance.
      const processor = blurProcessor || virtualBackgroundProcessor;
      // @ts-ignore
      if (!processor._isSimdEnabled) {
        await setCaptureConstraints();
      }

      if (backgroundSettings.type === 'blur') {
        addProcessor(blurProcessor);
      } else if (backgroundSettings.type === 'image' && typeof backgroundSettings.index === 'number') {
        virtualBackgroundProcessor.backgroundImage = await getImage(backgroundSettings.index, 0.65);
        addProcessor(virtualBackgroundProcessor);
      } else {
        removeProcessor();
      }
    };
    handleProcessorChange();
  }, [backgroundSettings, videoTrack, addProcessor, removeProcessor, setCaptureConstraints]);

  return [backgroundSettings, setBackgroundSettings, isPresentationMode, togglePresentationMode] as const;
}
